import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthResponse } from '../interfaces/authResponse';
import { PermissionsList } from '../enums/auth';
import { UserRole } from '../enums/userRoles';
import { PortalType } from '../enums/portalType';
import { UiComponentSetting } from '../enums/uiComponentSetting';
import { PermissionList } from '../enums/permissionList';
import { EnvConfigService } from '../../../shared/services/env-config.service';
import { LocalKeys } from '../../../shared/enums/common';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly env = inject(EnvConfigService);

  constructor() {
    if (!this.getQueryStringApiKey()) {
      this.loadStoredUser();
    }
  }
  //#region Params

  public refreshTokenInProgress: boolean = false;
  private readonly currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$: Observable<any> = this.currentUserSubject.asObservable();

  private readonly currentUserPermissions = new BehaviorSubject<any>(null);
  public currentUserPermissions$: Observable<any> = this.currentUserPermissions.asObservable();

  //#endregion

  //#region Methods
  storeApiKey(apiKey: string): void {
    localStorage.setItem(LocalKeys.apiKey, apiKey);
    const objKey = JSON.parse(apiKey);
    localStorage.setItem(LocalKeys.permissions, JSON.stringify(objKey.permissions));
    localStorage.setItem(LocalKeys.portalType, objKey.portal_type); //1 admin, 2 client
    localStorage.setItem(LocalKeys.role, objKey.role.toLocaleLowerCase());
  }
  //role

  appSetting(): any {
    const portalType = localStorage.getItem(LocalKeys.portalType);
    const role = localStorage.getItem(LocalKeys.role);
    //first security layer to validate the portal type with role
    if ((role == UserRole.Admin.toLocaleLowerCase() && Number.parseInt(portalType!) == PortalType.Client) || (role == UserRole.Client.toLocaleLowerCase() && Number.parseInt(portalType!) == PortalType.Admin)) return null;

    const portalPermissions: { permissionId: number; title: string }[] = JSON.parse(localStorage.getItem(LocalKeys.portalPermissions) ?? '[]');

    const permissions = JSON.parse(localStorage.getItem('permissions') ?? '[]');
    return {
      hasPendingRidesPermission: role == UserRole.Admin.toLocaleLowerCase(),
      hasMassCancellation: portalPermissions.some(perm => perm.title == UiComponentSetting.MassCancellation) && permissions.includes(PermissionList.MassCancellation), //mix between json file config + user permissions
      hasMassNotificationToGuardians: role == UserRole.Admin.toLocaleLowerCase(),
      hasSendNotificationToAll: portalPermissions.some(perm => perm.title == UiComponentSetting.SendGroupMessage) && permissions.includes(PermissionList.SendGroupMessage), //mix between json file config + user permissions
      hasSendScheduledNotification: portalPermissions.some(perm => perm.title == UiComponentSetting.SendGroupMessage) && permissions.includes(PermissionList.SendGroupMessage),
      hasCancelStudentRide: portalPermissions.some(perm => perm.title == UiComponentSetting.CancelStudentRide),
      hasAutoStartPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.AutoStart),
      hasAutoAcceptRidePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.AutoAccept),
      hasAssignSubDriverPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.AssignSubDriver),
      hasSendRideOfferPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.SendRideOffer),
      hasResetRidePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ResetRide),
      hasUnassignDriverPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.UnassignDriver),
      hasIgnoreDoubleBookedPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.IgnoreDriverDoubleBooked),
      hasIgnoreMonitorDoubleBookedPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.IgnoreMonitorDoubleBooked),
      hasPayoutAdjustmentPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.AddPayoutAdjustment),
      hasFindDriverPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.FindDriver),
      hasChangeRideTimePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ChangeRideTime),
      hasViewWaitingTimePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.WaitingTime),
      hasViewRideActivityPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.RideActivity),
      hasCompleteRidePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.CompleteRide),
      hasDeleteRidePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.DeleteRide),
      hasAddIncidentReportPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.AddIncidentReport),
      hasSendDelayNotificationPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.SendDelayNotification),
      hasChangeStopCargoStatus: portalPermissions.some(perm => perm.title == UiComponentSetting.ChangeStopCargoStatus),

      hasResetStopPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ResetStop),
      hasSendGroupMessagePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.SendGroupMessage),
      hasDriverMessagingPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowDriverMessaging),
      hasGuardianMessagingPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowGuardianMessaging),
      hasRideStopsActivityLogPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.StopsActivityLog),
      hasCancelTransportationPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.CancelTransportation),
      hasChangeCancellationTypePermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ChangeCancellationType),
      hasClientFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowClientFilter),
      hasMultiClientFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowClientIdsFilter),
      hasContractorFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowContractorFilter),
      hasDriverProviderFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowDriverProviderFilter),
      hasFromTimeFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowFromTimeFilter),
      hasToTimeFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowToTimeFilter),
      hasPassengerFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowPassengerFilter),
      hasPeriodFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowPeriodFilter),
      hasPlaceFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowPlaceFilter),
      hasRideIdFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowRideIdFilter),
      hasServiceAreaFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowServiceAreaFilter),
      hasServiceRegionFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowServiceRegionFilter),
      hasShowAllFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowShowAllFilter),
      hasStatusFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowStatusFilter),
      hasRidesWithNotesPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowWithNotesFilter),
      hasEquipmentDetailsPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowEquipmentGridIcon),
      hasCancellationReasonFilterPermission: role?.toLowerCase() == UserRole.Admin.toLocaleLowerCase(),

      viewContractorBasicInfo: portalPermissions.some(perm => perm.title == UiComponentSetting.viewContractorBasicInfo),
      viewDriverProviderContacts: portalPermissions.some(perm => perm.title == UiComponentSetting.viewDriverProviderContacts),
      viewBankInformation: portalPermissions.some(perm => perm.title == UiComponentSetting.viewBankInformation),
      viewVehicleDetails: portalPermissions.some(perm => perm.title == UiComponentSetting.viewVehicleDetails),
      viewContractorNotificatoins: portalPermissions.some(perm => perm.title == UiComponentSetting.viewContractorNotificatoins),
      viewContractorDeviceInfo: portalPermissions.some(perm => perm.title == UiComponentSetting.viewContractorDeviceInfo),
      viewBPSheetDetails: portalPermissions.some(perm => perm.title == UiComponentSetting.viewBPSheetDetails),
      viewBPSheetStops: portalPermissions.some(perm => perm.title == UiComponentSetting.viewBPSheetStops),
      viewBPSheetEquipment: portalPermissions.some(perm => perm.title == UiComponentSetting.viewBPSheetEquipment),
      viewBPSheetClanedars: portalPermissions.some(perm => perm.title == UiComponentSetting.viewBPSheetClanedars),
      viewClientContacts: portalPermissions.some(perm => perm.title == UiComponentSetting.viewClientContacts),
      viewDestinationTable: portalPermissions.some(perm => perm.title == UiComponentSetting.viewDestinationTable),
      viewStudentsTable: portalPermissions.some(perm => perm.title == UiComponentSetting.viewStudentsTable),
      viewPaymentDetails: portalPermissions.some(perm => perm.title == UiComponentSetting.viewPaymentDetails),
      viewPlaceInstructions: portalPermissions.some(perm => perm.title == UiComponentSetting.viewPlaceInstructions),
      viewNoteActivityLog: portalPermissions.some(perm => perm.title == UiComponentSetting.viewNoteActivityLog),
      viewActivityLog: portalPermissions.some(perm => perm.title == UiComponentSetting.viewActivityLog),
      viewGPSPingsData: portalPermissions.some(perm => perm.title == UiComponentSetting.viewGPSPingsData),
      viewStopsActivityLog: portalPermissions.some(perm => perm.title == UiComponentSetting.viewStopsActivityLog),
      viewChangeTimeZoneDll: portalPermissions.some(perm => perm.title == UiComponentSetting.viewChangeTimeZoneDll),
      hasStateFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowStateFilter),
      hasEquipmentsNeedFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowEquipmentNeedFilter),
      hasEquipmentsFilterPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.ShowEquipmentTypeFilter),
      hasCameraIconPermission: portalPermissions.some(perm => perm.title == UiComponentSetting.hasCameraIconPermission),
    };
  }

  getApiKey(): string | null {
    return localStorage.getItem(LocalKeys.apiKey);
  }

  login({ email, password }: { email: string; password: string }): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.env.getConfig().apiUrl}/authentication/login`, {
        username: email,
        password,
      })
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(this.handleError),
      );
  }

  logout(): void {
    this.removeAuthData();
    this.currentUserSubject.next(null);
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigate(['/dispatch/auth/login']);
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    return !!token && !this.isTokenExpired(token);
  }

  refreshToken(): Observable<AuthResponse> {
    this.refreshTokenInProgress = true;
    const refreshToken = localStorage.getItem(LocalKeys.refreshToken);
    const accessToken = localStorage.getItem(LocalKeys.authToken);
    if (!refreshToken) {
      throw new Error('No refresh token available.');
    }
    const refreshUrl = `${this.env.getConfig().apiUrl}/authentication/refresh-token`;
    return this.http.post<AuthResponse>(refreshUrl, { accessToken, refreshToken }).pipe(
      tap(response => this.handleAuthSuccess(response)),
      catchError(this.handleError),
    );
  }
  getAuthToken(): string | null {
    return localStorage.getItem(LocalKeys.authToken);
  }

  getIdToken(): string | null {
    return localStorage.getItem(LocalKeys.idToken);
  }

  checkAccess(pageName: string): boolean {
    if (this.getQueryStringApiKey() != null) {
      let permissions = JSON.parse(localStorage.getItem(LocalKeys.permissions)!) as any[];
      const pageId = PermissionsList[pageName as keyof typeof PermissionsList];
      if (permissions.includes(pageId)) {
        return true;
      }
      return false;
    }
    return true;
  }
  //#endregion

  //#region Helpers

  private storeRefreshToken(refreshToken: string): void {
    localStorage.setItem(LocalKeys.refreshToken, refreshToken);
  }

  private storeIdToken(idToken: string): void {
    localStorage.setItem(LocalKeys.idToken, idToken);
  }

  private storeUserDetails(user: any): void {
    localStorage.setItem(LocalKeys.user, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private storeExpirationToken(expiration: string): void {
    let user = JSON.parse(localStorage.getItem(LocalKeys.user)!);
    user.expiration = expiration;
    localStorage.setItem(LocalKeys.user, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  private storeToken(token: string): void {
    localStorage.setItem(LocalKeys.authToken, token);
  }

  private storePermissions(permissions: any): void {
    localStorage.setItem(LocalKeys.permissions, permissions);
    this.currentUserPermissions.next(permissions);
  }

  private removeAuthData(): void {
    localStorage.removeItem(LocalKeys.authToken);
    localStorage.removeItem(LocalKeys.refreshToken);
    localStorage.removeItem(LocalKeys.idToken);
    localStorage.removeItem(LocalKeys.user);
    localStorage.removeItem(LocalKeys.permissions);
    localStorage.removeItem(LocalKeys.apiKey);
  }

  private handleAuthSuccess(response: AuthResponse): void {
    const { accessToken, refreshToken, idToken, expiration, permissions, userId, email, firstName, lastName } = response.data;
    accessToken && this.storeToken(accessToken);
    refreshToken && this.storeRefreshToken(refreshToken);
    idToken && this.storeIdToken(idToken);
    permissions && this.storePermissions(permissions);
    userId &&
      this.storeUserDetails({
        userId,
        email,
        firstName,
        lastName,
        expiration,
      });
    //to update expiration in refresh token
    if (!userId && expiration) {
      this.refreshTokenInProgress = false;
      this.storeExpirationToken(expiration);
    }
  }

  getPortalPermissions() {
    const url = `${this.env.getConfig().apiUrl}/authentication/ui-component-setting`;
    this.http.get(url).subscribe({
      next: (res: any) => {
        console.log(res);
        localStorage.setItem(LocalKeys.portalPermissions, JSON.stringify(res.data.permissions));
        localStorage.setItem(LocalKeys.role, res.data.role);
      },
      error: error => {
        console.log(error);
      },
    });
  }

  getQueryStringApiKey() {
    const params = new URLSearchParams(globalThis.location.search);
    const api_key = params.get('api_key');

    if (api_key) {
      this.storeApiKey(api_key);
      return api_key;
    }

    return this.getApiKey();
  }

  private decodeToken(token: string): any {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeToken(token);
    return decodedToken ? decodedToken.exp * 1000 < Date.now() : true;
  }

  private handleError(error: any): Observable<never> {
    return throwError(
      //add toaster here
      () => new Error('An error occurred. Please try again later.'),
    );
  }

  private loadStoredUser(): void {
    const userJson = localStorage.getItem(LocalKeys.user);
    if (userJson) {
      this.currentUserSubject.next(JSON.parse(userJson));
    }
  }
  //#endregion
}
